import { CSSProperties, forwardRef, useEffect, useRef } from "react";
import styles from "./style.module.scss";
import classNames from "classnames";

function mergeRefs<T = any>(...refs: (React.Ref<T> | undefined)[]) {
  return (value: T) => {
    refs.forEach((ref) => {
      if (typeof ref === "function") ref(value);
      else if (ref != null)
        (ref as React.MutableRefObject<T | null>).current = value;
    });
  };
}

type ScrollAwareHoverProps = JSX.IntrinsicElements["div"] & {
  className?: string;
  children: JSX.Element;
  style?: CSSProperties;
  onMouseOver: () => void;
  onMouseLeave: () => void;
};

const ScrollAwareHover = forwardRef<HTMLDivElement, ScrollAwareHoverProps>(
  ({ children, className, onMouseOver, onMouseLeave, ...props }, ref) => {
    const internalRef = useRef<HTMLDivElement | null>(null);
    const mousePos = useRef({ x: 0, y: 0 });
    const isInsideRef = useRef(false);

    const containerClassNames = classNames(styles.container, className);

    const handleScroll = () => {
      const refElement = internalRef.current;
      if (!refElement) return;

      const { x, y } = mousePos.current;
      const hoveredEl = document.elementFromPoint(x, y);
      if (!hoveredEl) return;

      const isInside = refElement.contains(hoveredEl);
      if (isInside && !isInsideRef.current) {
        isInsideRef.current = true;
        onMouseOver();
      } else if (!isInside && isInsideRef.current) {
        isInsideRef.current = false;
        onMouseLeave();
      }
    };

    useEffect(() => {
      const updateMouse = (e: MouseEvent) => {
        mousePos.current = { x: e.clientX, y: e.clientY };
      };
      window.addEventListener("mousemove", updateMouse);
      return () => window.removeEventListener("mousemove", updateMouse);
    }, []);

    useEffect(() => {
      const scrollEl = document.querySelector("#data-page-cards-list");
      scrollEl?.addEventListener("scroll", handleScroll);
      return () => scrollEl?.removeEventListener("scroll", handleScroll);
      //eslint-disable-next-line
    }, []);

    return (
      <div
        ref={mergeRefs(internalRef, ref)}
        className={containerClassNames}
        onMouseOver={onMouseOver}
        onMouseLeave={onMouseLeave}
        {...props}
      >
        {children}
      </div>
    );
  }
);

ScrollAwareHover.displayName = "ScrollAwareHover";

export default ScrollAwareHover;
